import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';

import {
  setNavigationAction,
  resetNavigationAction,
  setHeaderActions,
  resetHeaderActions,
} from 'app/shared-components/store/headerSlice';
import { BoxedTabs } from 'app/shared-components/structure';
import PlusIcon from 'app/assets/icons/plus.svg?react';
import PrintIcon from 'app/assets/icons/print.svg?react';
import BlockedOutCalendarIcon from 'app/assets/icons/blocked-out-calendar.svg?react';
import { useSelectedPharmacyId, useSelectedPharmacy } from 'app/hooks';
import useEventTracker from 'app/hooks/useEventTracker';
import { CALENDAR_EVENTS } from 'app/tracking';
import config from '../../../../config.json';

import useOverdueAppointmentsCount from '../hooks/useOverdueAppointmentsCount';
import useGetPharmacyFirstServiceById from '../hooks/useGetPharmacyFirstServiceById';
import { CalendarPrintProvider, useCalendarPrintContext } from '../print/CalendarPrintContext';
import CalendarPrintSheet from '../print/CalendarPrintSheet';
import useCalendarPrint from '../print/hooks/useCalendarPrint';

const tabs = [
  {
    label: 'Week',
    value: 'week-view',
    path: '/calendar',
  },
  {
    label: 'Agenda',
    value: 'agenda-view',
    path: '/calendar/agenda',
  },
  {
    label: 'Action Required',
    value: 'overdue-view',
    path: '/calendar/overdue',
  },
];

export default function CalendarLayout({ children }) {
  return (
    <CalendarPrintProvider>
      <CalendarLayoutContent>{children}</CalendarLayoutContent>
    </CalendarPrintProvider>
  );
}

function CalendarLayoutContent({ children }) {
  const trackEvent = useEventTracker();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPharmacyId = useSelectedPharmacyId();
  const selectedPharmacy = useSelectedPharmacy();
  const overdueCount = useOverdueAppointmentsCount();
  const { print, isPreparing, canPrint } = useCalendarPrint();
  const { job: printJob, clearJob: clearPrintJob } = useCalendarPrintContext();

  const { data: pharmacyFirstService } = useGetPharmacyFirstServiceById({
    pharmacyId: selectedPharmacyId,
  });

  const selectedTab = tabs.find((tab) => location.pathname.endsWith(tab.path));
  const selectedTabValue = selectedTab?.value;

  const tabsWithBadges = useMemo(() => {
    return tabs.map((tab) => {
      if (tab.value === 'overdue-view') return { ...tab, badge: overdueCount };
      return tab;
    });
  }, [overdueCount]);

  useEffect(() => {
    dispatch(setNavigationAction({ title: 'Calendar' }));

    const dropdownItems = [
      {
        id: 'service',
        title: 'Service',
        callback: () => {
          // Track appointment creation
          trackEvent(
            CALENDAR_EVENTS.APPOINTMENT_CREATED.action,
            CALENDAR_EVENTS.APPOINTMENT_CREATED
          );
          navigate('/appointments/create');
        },
      },
    ];

    // Only add pharmacy-first option if service exists (null means 404 was returned)
    if (pharmacyFirstService) {
      dropdownItems.push({
        id: 'pharmacy-first',
        title: 'Pharmacy First',
        callback: () => {
          // Track appointment creation (pharmacy first)
          trackEvent(
            CALENDAR_EVENTS.APPOINTMENT_CREATED.action,
            CALENDAR_EVENTS.APPOINTMENT_CREATED
          );
          navigate(`/appointments/create-pharmacy-first/${pharmacyFirstService.id}`);
        },
      });
    }

    const headerActions = [
      {
        id: 'print',
        type: 'button',
        title: isPreparing ? 'Preparing…' : 'Print',
        icon: <PrintIcon />,
        // Disabled while the current view has nothing to print or the rows are still loading
        disabled: !canPrint || isPreparing,
        callback: print,
      },
      ...(config.deploymentRegion !== 'canada'
        ? [
            {
              id: 'block-out-days',
              type: 'button',
              title: 'Block-out Days',
              icon: <BlockedOutCalendarIcon />,
              callback: () => {
                navigate(`/services/block-out-days`);
              },
            },
          ]
        : []),
      {
        id: 'create-appointment-dropdown',
        type: 'dropdown',
        title: 'Create',
        icon: <PlusIcon />,
        label: 'Create an appointment:',
        items: dropdownItems,
      },
    ];

    dispatch(setHeaderActions(headerActions));
    return () => {
      dispatch(resetNavigationAction());
      dispatch(resetHeaderActions());
    };
  }, [
    dispatch,
    navigate,
    trackEvent,
    selectedPharmacyId,
    selectedPharmacy,
    pharmacyFirstService,
    print,
    isPreparing,
    canPrint,
  ]);

  function handleTabChange(newValue) {
    // Track section clicked based on tab value
    const sectionEventMap = {
      'week-view': CALENDAR_EVENTS.WEEK.SECTION_CLICKED,
      'agenda-view': CALENDAR_EVENTS.AGENDA.SECTION_CLICKED,
      'overdue-view': CALENDAR_EVENTS.ACTION_REQUIRED.SECTION_CLICKED,
    };

    const event = sectionEventMap[newValue];
    if (event) {
      trackEvent(event.action, event);
    }

    const newPath = tabs.find((tab) => tab.value === newValue)?.path;
    if (newPath) navigate(newPath);
  }

  return (
    <>
      <Box className="grow grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] bg-white overflow-hidden p-16 gap-16">
        <Box>
          <BoxedTabs
            tabs={tabsWithBadges}
            selectedTab={selectedTabValue}
            handleChange={handleTabChange}
          />
        </Box>
        {children}
      </Box>
      {printJob && <CalendarPrintSheet printDocument={printJob} onDone={clearPrintJob} />}
    </>
  );
}
